<template>
	<div id="chart" ref="refChat"></div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	TitleComponent
} from 'echarts/components';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { useCommonData } from '@/hooks';
import dayjs from 'dayjs';
import { debounce } from '@/utils/api';
import { useModuleData } from '@/rebuild/hooks/data/useModuleData';
import { DEVICE_RESIZE_DELAY } from '@rebuild/hooks/useDeviceResize';

const { isFlowXTmpl } = useCommonData();

echarts.use([
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	CandlestickChart,
	LineChart,
	BarChart,
	CanvasRenderer,
	UniversalTransition,
	TitleComponent
]);

function splitData(rawData) {
	let categoryData = []; // [时间]
	let values = [];
	for (let i = 0; i < rawData.length; i++) {
		const { tradingDay, closePrice = 0 } = rawData[i];
		categoryData.push(dayjs(tradingDay).format('YYYY/MM/DD'));
		values.push(closePrice);
	}
	return {
		categoryData: categoryData,
		values: values
	};
}

const { quote_quoteKLine } = useModuleData();
const queryQuote = async () => {
	const defaultTime = 1;
	const daytime = new Date();
	let endTradingDay = daytime.getTime();
	let startTradingDay = daytime.setFullYear(daytime.getFullYear() - 1);

	await quote_quoteKLine.refresh({
		defaultTime,
		startTradingDay,
		endTradingDay
	});

	if (quote_quoteKLine.empty) {
		return [];
	}
	const arr = quote_quoteKLine.data?.data?.reverse() || [];
	return arr;
};
const { getUpperThemeColor } = useCommonData();
let chatInstance = null;
const refChat = ref();
const refresh = async () => {
	if (chatInstance) {
		chatInstance.dispose();
		chatInstance.clear();
		chatInstance = null;
	}

	const data = await queryQuote();
	const { categoryData, values } = splitData(data);

	chatInstance = echarts.init(refChat.value);

	// 获取坐标轴刻度最大值
	const maxVal = Number(Math.max(...values)) * 1.01;
	// 获取坐标轴刻度最小值
	const minVal = Number(Math.min(...values)) * 0.98;
	// 计算坐标轴分割间隔
	let interval = (maxVal - minVal) / 5;

	// 指定图表的配置项和数据
	const option = {
		grid: {
			top: '0%',
			left: '0%',
			right: '0%',
			height: '100%'
		},
		xAxis: {
			type: 'category',
			data: categoryData,
			axisLine: {
				show: false
			},
			axisLabel: {
				show: false
			}
		},
		yAxis: {
			type: 'value',
			axisLine: {
				show: false
			},
			axisLabel: {
				show: false
			},
			splitNumber: 5,
			interval: interval,
			splitLine: {
				lineStyle: { color: '#F2F3F5', type: 'dashed' },
				show: !isFlowXTmpl.value
			},
			min: minVal,
			max: maxVal
		},
		series: [
			{
				type: 'line',
				symbol: 'none',
				sampling: 'lttb',
				itemStyle: {
					color: getUpperThemeColor.value
				},
				areaStyle: {
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: getUpperThemeColor.value
						},
						{
							offset: 1,
							color: '#FFFFFF'
						}
					])
				},
				data: values
			}
		]
	};

	// 使用刚指定的配置项和数据显示图表。
	chatInstance.setOption(option);

	// 同步页面自适应缩放
	window.addEventListener('resize', resizeHandler);
};
const resizeHandler = debounce(() => {
	chatInstance && chatInstance.resize();
}, DEVICE_RESIZE_DELAY);
onBeforeUnmount(() => {
	window.removeEventListener('resize', resizeHandler);
});

defineExpose({
	refresh
});
</script>

<style lang="less" scoped>
#chart {
	width: 100%;
	height: 230px;
}
</style>
